import { clsx, type ClassValue } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

/**
 * tailwind-merge has to be told about this app's custom `fontSize` scale
 * (tailwind.config.js). Without it, a class like `text-display-md` is not recognised as a
 * font size and falls into the *text-colour* group instead — so merging it with a real
 * colour class silently drops whichever came first.
 *
 * NOTE — this app's key list is deliberately SHORTER than the portals'. `apps/web` defines
 * only the three `display-*` sizes; `heading-*`, `body-*`, `caption` and `overline` exist
 * in the admin and partner configs but NOT here. Copying their 11-key list would declare
 * font sizes this app has no CSS for. Keep this list in step with tailwind.config.js.
 *
 * Unlike the portals, nothing is currently broken by this: `cn()` has a single consumer
 * (`components/ui/Toast.tsx`) whose classes use built-in Tailwind sizes, and this app has
 * no shared Button component — its buttons build strings with `[...].join(' ')`, which
 * never reaches tailwind-merge. This config is here so that the obvious next refactor
 * (a shared Button, or migrating those join sites onto `cn`) does not silently inherit the
 * bug that hit the admin portal. `cn.test.ts` pins the behaviour.
 *
 * The config's other custom scales — `boxShadow`, `borderRadius`, `spacing`,
 * `transitionDuration` — are equally unknown to tailwind-merge, but only `text-*` has a
 * catch-all colour group to be misfiled into, so nothing is dropped there. They do fail to
 * *deduplicate* (`shadow-card shadow-lg` keeps both and CSS order decides); extend this
 * config the same way if a caller ever needs to override one through `cn()`.
 *
 * One case is NOT purely restorative: a custom size now correctly deletes a built-in size
 * or an arbitrary `text-[13px]` in the same merge, where previously both survived and CSS
 * order decided. No call site does that today, but it is the one way this config can
 * change rendering.
 */
const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      'font-size': [
        {
          text: ['display-lg', 'display-md', 'display-sm'],
        },
      ],
    },
  },
});

/**
 * Utility function to merge Tailwind CSS classes with clsx
 * Handles conditional classes and deduplication
 *
 * @param inputs - Class values to merge
 * @returns Merged class string
 *
 * @example
 * cn('px-4 py-2', 'bg-primary-500', isActive && 'text-white')
 * cn('px-4', { 'bg-red-500': hasError, 'bg-green-500': isSuccess })
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}
